import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useStructuredData } from '@/hooks/useStructuredData';

const PRIVACY_SCHEMAS = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Privacy Policy — Onetvservices',
    url: 'https://onetvservices.com/privacy',
    description: 'Onetvservices Privacy Policy — how we collect, use, and protect your personal data in compliance with GDPR and applicable privacy laws.',
    dateModified: '2026-04-03',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://onetvservices.com' },
        { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: 'https://onetvservices.com/privacy' },
      ],
    },
    publisher: {
      '@type': 'Organization',
      name: 'Onetvservices',
      url: 'https://onetvservices.com',
    },
  },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useStructuredData(PRIVACY_SCHEMAS);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="bg-gray-900 border-b border-white/10 py-6 px-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors cursor-pointer">
            <i className="ri-arrow-left-line text-lg" />
            <span className="text-sm">Back to Home</span>
          </Link>
          <div className="w-px h-5 bg-white/20" />
          <Link to="/">
            <img
              src="https://storage.readdy-site.link/project_files/ea087323-c332-4d1a-a768-642201e38806/8e03886a-a466-4209-8fc0-30625673693c_ONETV.png?v=410354e2b00bf17cf48639a5651635ec"
              alt="Onetvservices Logo"
              className="h-12 w-auto object-contain"
            />
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Title */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 text-primary text-sm font-medium mb-6">
            <i className="ri-shield-check-line" />
            Legal Document
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-white/50 text-sm">Last updated: April 3, 2026</p>
        </div>

        <div className="space-y-10 text-white/80 leading-relaxed">

          {/* Intro */}
          <section>
            <p className="text-lg">
              At <strong className="text-white">Onetvservices</strong>, we are committed to protecting your personal data and respecting your privacy. This Privacy Policy explains how we collect, use, and protect the information you provide when using our IPTV streaming service.
            </p>
          </section>

          {/* 1 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-primary/20 text-primary rounded-lg text-sm font-bold">1</span>
              Information We Collect
            </h2>
            <p className="mb-4">We may collect the following types of personal information:</p>
            <ul className="space-y-3">
              {[
                { icon: 'ri-user-line', text: 'Full name and contact details (phone number, email address)' },
                { icon: 'ri-map-pin-line', text: 'Country of residence' },
                { icon: 'ri-device-line', text: 'Device information (type, operating system, IP address)' },
                { icon: 'ri-bank-card-line', text: 'Payment information (processed securely via third-party providers)' },
                { icon: 'ri-chat-3-line', text: 'Communication history (WhatsApp, email support exchanges)' },
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-primary`} />
                  </div>
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-primary/20 text-primary rounded-lg text-sm font-bold">2</span>
              How We Use Your Information
            </h2>
            <p className="mb-4">Your personal data is used exclusively for the following purposes:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: 'ri-settings-3-line', title: 'Service Delivery', desc: 'Activating and managing your subscription' },
                { icon: 'ri-customer-service-2-line', title: 'Customer Support', desc: 'Responding to your requests and technical issues' },
                { icon: 'ri-secure-payment-line', title: 'Payment Processing', desc: 'Handling transactions securely' },
                { icon: 'ri-mail-send-line', title: 'Communications', desc: 'Sending important service updates' },
                { icon: 'ri-bar-chart-line', title: 'Service Improvement', desc: 'Analysing usage to enhance our platform' },
                { icon: 'ri-shield-check-line', title: 'Legal Compliance', desc: 'Meeting our legal and regulatory obligations' },
              ].map((item, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-primary/10 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-primary`} />
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">{item.title}</p>
                    <p className="text-white/60 text-sm mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-primary/20 text-primary rounded-lg text-sm font-bold">3</span>
              Data Sharing
            </h2>
            <p className="mb-4">
              We do <strong className="text-white">not sell, rent, or trade</strong> your personal information to third parties. We may share data only with:
            </p>
            <ul className="space-y-2">
              {[
                'Trusted payment processors (e.g., PayPal, Stripe) to handle transactions',
                'Technical service providers who assist in operating our platform',
                'Legal authorities when required by applicable law',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-primary mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-primary/20 text-primary rounded-lg text-sm font-bold">4</span>
              Data Security
            </h2>
            <p>
              We implement industry-standard security measures to protect your personal data against unauthorised access, alteration, disclosure, or destruction. All payment transactions are encrypted using SSL technology. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-primary/20 text-primary rounded-lg text-sm font-bold">5</span>
              Data Retention
            </h2>
            <p>
              We retain your personal data for as long as your account is active or as needed to provide you with our services. You may request deletion of your data at any time by contacting us. We will comply within 30 days, unless retention is required by law.
            </p>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-primary/20 text-primary rounded-lg text-sm font-bold">6</span>
              Your Rights
            </h2>
            <p className="mb-4">You have the following rights regarding your personal data:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { right: 'Right of Access', desc: 'Request a copy of your data' },
                { right: 'Right to Rectification', desc: 'Correct inaccurate information' },
                { right: 'Right to Erasure', desc: 'Request deletion of your data' },
                { right: 'Right to Portability', desc: 'Receive your data in a portable format' },
                { right: 'Right to Object', desc: 'Object to certain processing activities' },
                { right: 'Right to Withdraw Consent', desc: 'Withdraw consent at any time' },
              ].map((item, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-3 flex items-center gap-3">
                  <i className="ri-check-double-line text-primary flex-shrink-0" />
                  <div>
                    <p className="text-white text-sm font-semibold">{item.right}</p>
                    <p className="text-white/50 text-xs">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-primary/20 text-primary rounded-lg text-sm font-bold">7</span>
              Cookies
            </h2>
            <p>
              Our website may use cookies to improve your browsing experience. Cookies are small text files stored on your device. You can control cookie settings through your browser preferences. Disabling cookies may affect some features of our website.
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-primary/20 text-primary rounded-lg text-sm font-bold">8</span>
              Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page with an updated date. We encourage you to review this policy periodically.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-3">Contact Us</h2>
            <p className="text-white/70 mb-6">
              For any questions or concerns regarding this Privacy Policy or your personal data, please contact us:
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="mailto:onetvservices@gmail.com"
                className="flex items-center gap-3 bg-white/10 hover:bg-white/15 border border-white/10 rounded-xl px-5 py-3 transition-colors cursor-pointer"
              >
                <i className="ri-mail-line text-primary text-lg" />
                <span className="text-sm font-medium">onetvservices@gmail.com</span>
              </a>
              <a
                href="https://wa.me/12089746525"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-green-500/10 hover:bg-green-500/20 border border-green-500/20 rounded-xl px-5 py-3 transition-colors cursor-pointer"
              >
                <i className="ri-whatsapp-line text-green-400 text-lg" />
                <span className="text-sm font-medium">WhatsApp Support</span>
              </a>
            </div>
          </section>

        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-white/10 py-8 px-4 text-center text-white/40 text-sm">
        <p>Onetvservices 2019-2026 © All Rights Reserved</p>
        <div className="flex items-center justify-center gap-4 mt-3">
          <Link to="/" className="hover:text-white/70 transition-colors cursor-pointer">Home</Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-white/70 transition-colors cursor-pointer">Terms &amp; Conditions</Link>
          <span>·</span>
          <Link to="/privacy" className="text-primary cursor-pointer">Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
}
